import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Sparkles, Plus, Play, Edit, Power } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/hooks/useConfirm";

export default function CelebrationEventsIndex({ celebration_events = [] }) {
  const { confirm, ConfirmDialog } = useConfirm();

  const handleToggleActive = async (eventId, isActive) => {
    const confirmed = await confirm({
      title: isActive ? 'Desactivar evento' : 'Activar evento',
      message: `¿Estás seguro de ${isActive ? 'desactivar' : 'activar'} este evento?`,
      confirmText: isActive ? 'Desactivar' : 'Activar',
      variant: isActive ? 'warning' : 'success'
    });

    if (!confirmed) return;

    router.post(`/admin/celebration_events/${eventId}/toggle_active`, {}, {
      preserveScroll: true
    });
  };

  const handleProcessNow = async (eventId) => {
    const confirmed = await confirm({
      title: 'Ejecutar evento ahora',
      message: '¿Estás seguro de ejecutar este evento manualmente? Esto procesará inmediatamente todos los usuarios elegibles.',
      confirmText: 'Ejecutar',
      variant: 'warning'
    });

    if (!confirmed) return;

    router.post(`/admin/celebration_events/${eventId}/process_now`, {}, {
      preserveScroll: true
    });
  };

  const getEventEmoji = (eventType) => {
    const emojis = {
      birthday: '🎂',
      work_anniversary: '🎉',
      new_user: '👋',
      custom_date: '📅'
    };
    return emojis[eventType] || '🎁';
  };

  const getEventLabel = (eventType) => {
    const labels = {
      birthday: 'Cumpleaños',
      work_anniversary: 'Aniversario Laboral',
      new_user: 'Usuario Nuevo',
      custom_date: 'Fecha Personalizada'
    };
    return labels[eventType] || eventType;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-8 h-8 text-purple-500" />
                Eventos de Celebración
              </h1>
              <p className="text-gray-600 mt-1">
                Gestiona los eventos automáticos que otorgan puntos a los usuarios
              </p>
            </div>
            <Button
              onClick={() => router.visit('/admin/celebration_events/new')}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo evento
            </Button>
          </div>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {celebration_events.map((event) => (
            <Card key={event.id} className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-xl">
                    <span className="text-3xl mr-3">{getEventEmoji(event.trigger_type)}</span>
                    <div>
                      <div>{getEventLabel(event.trigger_type)}</div>
                      <div className="text-sm text-gray-500 font-normal">
                        {event.hashtag}
                      </div>
                    </div>
                  </CardTitle>
                  <Badge className={cn(
                    "text-xs",
                    event.active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  )}>
                    {event.active ? "✅ Activo" : "⏸️ Inactivo"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Puntos */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4 border border-orange-200">
                  <div className="text-sm text-orange-600 font-medium">Puntos a Otorgar</div>
                  <div className="text-2xl font-bold text-orange-700">{event.points_amount} pts</div>
                </div>

                {/* Mensaje */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="text-xs text-gray-600 font-medium mb-1">Mensaje</div>
                  <div className="text-sm text-gray-700 italic">"{event.message_template}"</div>
                </div>

                {/* Acciones */}
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => router.visit(`/admin/celebration_events/${event.id}/edit`)}
                    className="flex-1 text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleToggleActive(event.id, event.active)}
                    className={cn(
                      "flex-1",
                      event.active
                        ? "text-yellow-600 hover:bg-yellow-50"
                        : "text-green-600 hover:bg-green-50"
                    )}
                  >
                    <Power className="w-4 h-4 mr-1" />
                    {event.active ? 'Desactivar' : 'Activar'}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleProcessNow(event.id)}
                    className="flex-1 text-purple-600 hover:bg-purple-50"
                    disabled={!event.active}
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Ejecutar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {celebration_events.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay eventos configurados
              </h3>
              <p className="text-gray-500 mb-4">
                Crea tu primer evento de celebración para comenzar
              </p>
              <Button
                onClick={() => router.visit('/admin/celebration_events/new')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Evento
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="mt-6">
          <Button
            variant="ghost"
            onClick={() => router.visit('/admin')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Volver al Panel de Admin
          </Button>
        </div>
      </div>

      <ConfirmDialog />
    </div>
  );
}
